// 先代の姫の弱攻撃「数えながら突く」を模型で（2026-11-04・駅0″）。
//   作者（11-04）「弱攻撃は主人公と同じ前方の突き。突きながら指を折り、それを次の技の合図にする」
//   「鶏の合図のように、何回指を折ったかで分かる形にする」。体は LUNAIRE と同じ（脚は腰・膝・足首の3つ・細剣）。
//   拍は主人公の弱攻撃（src/player_attack_timing.ts の light＝当たり 160〜220ms・全体 420ms）を、ボスが読ませる分だけ伸ばした仮の値。
//   流れ：構え（左手に5本立てる）→1回目の突きで1本折る→2回目の突きで2本目を折る→指を見せて溜め→合図の技（大きく踏み込む突き）。
//   指の本数（3本残り）が次の技を表す、という見せ方の型。どの本数で何が来るかは駅1で決める。

import { humanPose } from '../body'
import { bakeTracks, type MockupMove, type Tracks, type Vec } from '../core'

/** 拍（ms）。jabHit＝突きの当たり、fold＝指を折り終える時刻。 */
export const PRINCESS_JAB_BEAT = { jab1Hit: 660, jab2Hit: 1260, fold1: [500, 660], fold2: [1100, 1260], tellFrom: 1600, lungeHit: 1900, endMs: 3000 }
const B = PRINCESS_JAB_BEAT
const DUR = B.endMs

/** 構え：細剣を前へ・奥の手（左手）は顔の横に上げて指を見せる。 */
const STAND = { px: 0, py: -73, lean: 6, tw: 0, ht: 0, aF: 40, bF: 10, sF: 4, aB: -40, bB: -110, sB: 0, fF: [12, 0] as Vec, fB: [-10, 0] as Vec, nUp: 5 }
type K = Parameters<typeof humanPose>[0]
const build = (v: Record<string, number | Vec>) => {
  const r = { ...STAND, ...v }
  return { ...humanPose(r as unknown as K), nUp: r.nUp as number }
}

/** 足を1歩運ぶ：真上へ上げてから運び、真上から下ろす（着いたまま前後へ動かさない・どの区切りも io で速さが跳ねない）。 */
const step = (t0: number, t1: number, x0: number, x1: number, h: number): [number, Vec, 'io'][] => {
  const d = t1 - t0, dx = x1 - x0
  return [[t0, [x0, 0], 'io'], [t0 + d * 0.3, [x0 + dx * 0.15, -h], 'io'], [t1 - d * 0.3, [x1 - dx * 0.15, -h * 0.7], 'io'], [t1, [x1, 0], 'io']]
}

/** 1回分の突き（振りかぶり→突き→戻り）。at＝当たりの時刻。 */
const jab = (at: number, back: number): Partial<Record<keyof typeof STAND, [number, number | Vec, ('io' | 'in' | 'out')?][]>> => ({
  // 戻り（at＋260）は次の突きの振りかぶり（at−240）や合図（tellFrom）と重ねない（同じ時刻に置くと1コマで跳ぶ）
  px: [[at - 240, back, 'io'], [at - 100, back - 2, 'io'], [at, back + 10, 'in'], [at + 260, back, 'io']],
  lean: [[at - 100, 0, 'io'], [at, 14, 'in'], [at + 260, 6, 'io']],
  tw: [[at - 100, -18, 'io'], [at, 22, 'in'], [at + 260, 0, 'io']],
  aF: [[at - 100, 120, 'io'], [at, 2, 'in'], [at + 260, 40, 'io']],
  bF: [[at - 100, 10, 'io'], [at, -4, 'in'], [at + 260, 10, 'io']],
  sF: [[at - 100, 0, 'io'], [at, 2, 'in'], [at + 260, 4, 'io']],
  // 前足は持ち上げて踏み出し、踏み出したまま当てて、持ち上げて戻す（すり足に見せない）
  fF: [...step(at - 160, at, 12 + back, 28 + back, 12), ...step(at + 200, at + 380, 28 + back, 12 + back, 9)],
})
const j1 = jab(B.jab1Hit, 0), j2 = jab(B.jab2Hit, 0)
const cat = (k: keyof typeof STAND, extra: Tracks[string] = []): Tracks[string] =>
  [[0, STAND[k] as number | Vec], ...(j1[k] ?? []), ...(j2[k] ?? []), ...extra] as Tracks[string]

const TR: Tracks = {
  // 合図の後：腰を落として溜め→大きく踏み込んで突く→戻る
  px: cat('px', [[B.tellFrom, 10, 'io'], [B.lungeHit - 80, 4, 'io'], [B.lungeHit, 40, 'in'], [2300, 40], [2700, 10, 'io'], [2950, 0, 'io']]),
  py: [[0, -73], [B.tellFrom, -73], [B.lungeHit - 80, -66, 'io'], [B.lungeHit, -56, 'in'], [2300, -56], [2700, -73, 'io']],
  lean: cat('lean', [[B.tellFrom, 2, 'io'], [B.lungeHit - 80, -4, 'io'], [B.lungeHit, 26, 'in'], [2300, 26], [2700, 6, 'io']]),
  tw: cat('tw', [[B.tellFrom, -10, 'io'], [B.lungeHit - 80, -30, 'io'], [B.lungeHit, 30, 'in'], [2300, 30], [2700, 0, 'io']]),
  aF: cat('aF', [[B.tellFrom, 70, 'io'], [B.lungeHit - 80, 150, 'io'], [B.lungeHit, -6, 'in'], [2300, -6], [2700, 40, 'io']]),
  bF: cat('bF', [[B.tellFrom, 20, 'io'], [B.lungeHit - 80, 0, 'io'], [B.lungeHit, -10, 'in'], [2300, -10], [2700, 10, 'io']]),
  sF: cat('sF', [[B.tellFrom, 6, 'io'], [B.lungeHit - 80, -4, 'io'], [B.lungeHit, -2, 'in'], [2300, -2], [2700, 4, 'io']]),
  // 奥の手：突きの間も顔の横に残して指を見せ、溜めで前へ突き出して本数を見せつける。踏み込みでは後ろへ流す。
  aB: [[0, -40], [B.tellFrom - 120, -40], [B.tellFrom + 80, -10, 'io'], [B.lungeHit - 80, -20, 'io'], [B.lungeHit + 80, 150, 'io'], [2300, 150], [2700, -40, 'io']],
  bB: [[0, -110], [B.tellFrom - 120, -110], [B.tellFrom + 80, -30, 'io'], [B.lungeHit - 80, -40, 'io'], [B.lungeHit + 80, 140, 'io'], [2300, 140], [2700, -110, 'io']],
  nUp: [[0, 5], [B.fold1[0], 5], [B.fold1[1], 4, 'io'], [B.fold2[0], 4], [B.fold2[1], 3, 'io'], [2400, 3], [2700, 5, 'io']],
  // 戻りは片足ずつ：後ろ足→前足→（構えの幅へ）前足→後ろ足。両足が同時に浮かないように。
  fF: cat('fF', [...step(B.lungeHit - 150, B.lungeHit, 12, 64, 12), ...step(2420, 2620, 64, 24, 10), ...step(2760, 2850, 24, 12, 8)]),
  fB: [[0, [-10, 0]], ...step(2300, 2420, -10, 4, 9), ...step(2850, 2960, 4, -10, 7)],
}

export const PRINCESS_COUNT_JAB_MOCKUP: MockupMove = {
  id: 'princess-count-jab', boss: '先代の姫', label: '弱攻撃・数えながら突く（突くたびに指を1本折る→本数が次の技の合図）',
  body: 'human-rapier-skirt', durMs: DUR, scale: 0.95, baseX: 300, heroAt: 150, interp: 'linear',
  keys: bakeTracks(TR, DUR, build),
  phases: [
    { from: 0, to: 400, label: '構え：左手に5本立てて見せる', kind: 'rest' },
    { from: 400, to: 1000, label: '1回目の突き・指を1本折る（残り4）', kind: 'hit' },
    { from: 1000, to: 1600, label: '2回目の突き・もう1本折る（残り3）', kind: 'hit' },
    { from: 1600, to: B.lungeHit, label: '合図：3本を突き出して見せ、腰を落とす', kind: 'tell' },
    { from: B.lungeHit, to: 2300, label: '合図の技：大きく踏み込んで突く', kind: 'hit' },
    { from: 2300, to: DUR, label: '戻り・指を開き直す', kind: 'rest' },
  ],
  hits: [B.jab1Hit, B.jab2Hit, B.lungeHit], activeMs: 60, box: [40, 120, -128, -88], paths: ['sF'],
  cues: [{ at: B.fold1[1], ms: 80 }, { at: B.fold2[1], ms: 80 }],
  source: '主人公の弱攻撃（当たり 0.16〜0.22秒・全体 0.42秒）を元に、ボスが読ませる分だけ伸ばした仮の拍。帯の黄色＝指を折り終えた瞬間。数値は技を組む時と数値を詰める時に決め直す。',
}
